import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useTranslation } from 'react-i18next';

import { SUPPORTED_LOCALES, setLocale, type AppLocale } from '@saas-kit/lib';

import { CheckIcon, GlobeIcon } from './icons';

/** Topbar control for switching the UI language. Selecting a locale also
 *  flips <html dir> via setLocale, so RTL languages reflow immediately. */
export function LanguageToggle(): JSX.Element {
  const { t, i18n } = useTranslation('common');
  const active = i18n.language;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={t('nav.language')}
          data-testid="language-toggle"
          data-locale={active}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <GlobeIcon size={18} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-[10rem] overflow-hidden rounded-md border border-border bg-popover p-1 shadow-md"
        >
          <DropdownMenu.Label className="px-2 py-1 text-xs text-muted-foreground">
            {t('nav.language')}
          </DropdownMenu.Label>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          {SUPPORTED_LOCALES.map((locale) => (
            <DropdownMenu.Item
              key={locale.code}
              onSelect={() => setLocale(locale.code as AppLocale)}
              data-testid={`language-option-${locale.code}`}
              className="flex cursor-pointer items-center justify-between gap-3 rounded px-2 py-1.5 text-sm outline-none focus:bg-muted"
            >
              <span>{locale.label}</span>
              {locale.code === active ? (
                <CheckIcon size={14} className="text-primary" />
              ) : null}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
